import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import type { Module } from "@bios/types";
import { Card, Pill, RealityScoreCard } from "@bios/ui";
import { useApiAuthed, useApiDev } from "../auth.js";
import type { ItemDetailResponse } from "../api.js";

interface PageProps {
  devModeNoAuth?: boolean;
}

export function ItemDetail({ devModeNoAuth }: PageProps) {
  const { module = "media", id = "" } = useParams<{ module: Module; id: string }>();
  const api = devModeNoAuth ? useApiDev() : useApiAuthed();
  const [data, setData] = useState<ItemDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.item(id).then(setData).catch((e) => setError(String(e)));
  }, [api, id]);

  if (error) return <Card><p style={{ color: "var(--bios-noise)", fontSize: 13 }}>{error}</p></Card>;
  if (!data) return <Card><p style={{ color: "var(--bios-text-muted)" }}>Loading…</p></Card>;

  return (
    <>
      <div className="bios-page-header">
        <div>
          <Link to={`/app/${module}/items`} style={{ fontSize: 12, color: "var(--bios-text-muted)" }}>← Back to items</Link>
          <h1 className="bios-page-title" style={{ marginTop: 6 }}>{data.item.title}</h1>
          <p className="bios-page-sub">
            <a href={data.item.url} target="_blank" rel="noreferrer">{data.item.url}</a>
          </p>
        </div>
        <Pill variant={data.item.status === "scored" ? "signal" : "default"}>{data.item.status}</Pill>
      </div>

      <div className="bios-grid cols-3">
        <div style={{ gridColumn: "span 2" }}>
          <Card title="Content">
            <p style={{ fontSize: 14, lineHeight: 1.6, color: "var(--bios-text)", whiteSpace: "pre-wrap" }}>
              {data.item.body ?? "(no body extracted)"}
            </p>
            <div className="bios-divider" />
            <div style={{ fontSize: 12, color: "var(--bios-text-muted)" }}>
              {data.item.author && <span style={{ marginRight: 16 }}>By {data.item.author}</span>}
              {data.item.published_at && <span>Published {new Date(data.item.published_at).toLocaleString()}</span>}
            </div>
          </Card>

          {data.classification && (
            <Card title="Classification" className="" >
              <div style={{ display: "grid", gap: 12 }}>
                <Row label="Activity" value={<Pill>{data.classification.activity_type}</Pill>} />
                <Row
                  label="Topics"
                  value={
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {data.classification.topics.length > 0 ? (
                        data.classification.topics.map((t) => <Pill key={t}>{t}</Pill>)
                      ) : (
                        <span style={{ fontSize: 12, color: "var(--bios-text-faint)" }}>—</span>
                      )}
                    </div>
                  }
                />
                <Row
                  label="Entities"
                  value={
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {data.classification.entities.length > 0 ? (
                        data.classification.entities.map((e) => <Pill key={e}>{e}</Pill>)
                      ) : (
                        <span style={{ fontSize: 12, color: "var(--bios-text-faint)" }}>—</span>
                      )}
                    </div>
                  }
                />
                <Row
                  label="Confidence"
                  value={<span style={{ fontFamily: "var(--bios-mono)", fontSize: 12 }}>{(data.classification.confidence * 100).toFixed(0)}%</span>}
                />
                <Row label="Model" value={<span style={{ fontFamily: "var(--bios-mono)", fontSize: 12 }}>{data.classification.model}</span>} />
              </div>
            </Card>
          )}

          {data.item_impact && (
            <Card title="Geography">
              <div style={{ display: "grid", gap: 12 }}>
                <Row
                  label="Scope"
                  value={
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      <Pill>{data.item_impact.geo_scope}</Pill>
                      {data.item_impact.geo_state && <Pill>{data.item_impact.geo_state}</Pill>}
                      {data.item_impact.geo_city && <Pill>{data.item_impact.geo_city}</Pill>}
                    </div>
                  }
                />
                <Row label="Confidence" value={<Pill>{data.item_impact.geo_confidence}</Pill>} />
                {data.item_impact.geo_confidence === "low" && (
                  <p style={{ margin: 0, fontSize: 12, color: "var(--bios-text-muted)" }}>
                    Location inferred — may not be precise
                  </p>
                )}
              </div>
            </Card>
          )}
        </div>

        <div>
          {data.reality_score ? (
            <RealityScoreCard
              realityScore={data.reality_score.reality_score}
              signalScore={data.reality_score.signal_score}
              noiseScore={data.reality_score.noise_score}
              evidenceScore={data.reality_score.evidence_score}
              noveltyScore={data.reality_score.novelty_score}
              sourceTrust={data.reality_score.source_trust}
              rationale={data.reality_score.rationale}
            />
          ) : (
            <Card title="Reality Score">
              <p style={{ color: "var(--bios-text-muted)", fontSize: 13 }}>Not yet scored.</p>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 12, alignItems: "center" }}>
      <span style={{ fontSize: 12, color: "var(--bios-text-muted)" }}>{label}</span>
      <div>{value}</div>
    </div>
  );
}
